<script lang="ts" setup>
import AlertComponent from "@/components/AlertComponent.vue";
import HasPermission from "@/components/HasPermission.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import RoundedBox from "@/components/RoundedBox.vue";
import ShadowBox from "@/components/ShadowBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledCombobox from "@/components/StyledCombobox.vue";
import StyledInput from "@/components/StyledInput.vue";
import StyledSelect from "@/components/StyledSelect.vue";
import useCountryCodes from "@/composables/useCountryCodes";
import useEmail from "@/composables/useEmail";
import usePhoneRequest from "@/composables/usePhoneRequest";
import { EmailTypes } from "@/enums/emailTypes";
import { PhoneTypes } from "@/enums/phoneTypes";
import { ContactType } from "@/interfaces/contactType";
import { Contact } from "@/interfaces/contacts";
import { Email } from "@/interfaces/email";
import { Phone } from "@/interfaces/phone";
import useContactEmails from "@/internal/composables/useContactEmails";
import useContactPhones from "@/internal/composables/useContactPhones";
import useContacts from "@/internal/composables/useContacts";
import { Icon } from "@iconify/vue";
import { useQueryClient } from "@tanstack/vue-query";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { computed, reactive, ref } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import useHotelVerifyCredit from "../composables/useHotelVerifyCredit";

interface Props {
  contact: Contact;
}

const route = useRoute();
const { filteredContactsType } = useHotelVerifyCredit(
  route.params.id as string,
);

const { countryCodes } = useCountryCodes({
  immediate: true,
});

const props = defineProps<Props>();

const toast = useToast();

const queryClient = useQueryClient();

const modalViewEmail = ref(false);

const actionViewEmail = () => {
  modalViewEmail.value = !modalViewEmail.value;
};

const modalViewPhone = ref(false);

const actionViewPhone = () => {
  modalViewPhone.value = !modalViewPhone.value;
};

const {
  updateContactAsync,
  deleteContactAsync,
  deleteContactLoading,
  restoreContactAsync,
  updateContactLoading,
} = useContacts({
  immediate: false,
});

const {
  createContactEmailAsync,
  createContactEmailLoading,
  updateContactEmailAsync,
  refetch: refetchEmails,
} = useContactEmails({
  immediate: false,
  params: {
    id: props.contact.id,
  },
});

const { updateEmailLoading } = useEmail({
  immediate: false,
});

const {
  createContactPhoneAsync,
  createContactPhoneLoading,
  updateContactPhoneAsync,
  refetch: refetchPhones,
  isFetching: isLoadingPhones,
} = useContactPhones({
  immediate: false,
  params: {
    id: props.contact.id,
  },
});

const { updatePhoneAsync, updatePhoneLoading } = usePhoneRequest({
  immediate: false,
});

const rules = {
  contactType: {
    required: helpers.withMessage("El tipo de contacto es requerido", required),
  },
  name: {
    required: helpers.withMessage("El nombre es requerido", required),
  },
  jobTitle: {
    required: helpers.withMessage("El puesto es requerido", required),
  },
  email: {
    required: helpers.withMessage("El correo es requerido", required),
    requiredIf: helpers.withMessage(
      "Debe ser un correo electrónico",
      (value: string) => {
        return value.includes("@");
      },
    ),
  },
  countryCode: {
    required: helpers.withMessage("El código de país es requerido", required),
  },
  phone: {
    required: helpers.withMessage("El teléfono es requerido", required),
    requiredIf: helpers.withMessage(
      "Debe ser el numero de 10 digitos",
      (value: string) => {
        return value.length === 10;
      },
    ),
  },
};

const formEmail = reactive({
  email: "",
});

const validationEmail = useVuelidate({ email: rules.email }, formEmail);

const handleCreateEmail = async () => {
  for (const field of validationEmail.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  const data = await createContactEmailAsync({
    id: props.contact.id,
    email: formEmail.email,
    type: "personal",
  });

  if (data) {
    await queryClient.invalidateQueries(["hotel-contacts"]);
    await refetchEmails();

    formEmail.email = "";
    actionViewEmail();
  }
};

const formPhone = reactive({
  phone: "",
  countryCode: "",
  extension: "",
});

const validationPhone = useVuelidate(
  { countryCode: rules.countryCode, phone: rules.phone },
  formPhone,
);

const handleCreatePhone = async () => {
  for (const field of validationPhone.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  const data = await createContactPhoneAsync({
    id: props.contact.id,
    phone: formPhone.phone,
    countryCodeId: Number(formPhone.countryCode),
    extension: formPhone.extension,
    type: formPhone.extension ? PhoneTypes.Oficina : PhoneTypes.Mobile,
  });

  if (data) {
    await queryClient.invalidateQueries(["hotel-contacts"]);
    await refetchPhones();

    formPhone.phone = "";
    formPhone.countryCode = "";

    actionViewPhone();
  }
};

const modalEditContact = ref(false);

const actionEditContact = () => {
  modalEditContact.value = !modalEditContact.value;
};

const contactTypes = ref<ContactType[]>([]);

const formEditContact = reactive({
  contactType: props.contact.contactTypes,
  name: props.contact.name,
  jobTitle: props.contact.jobTitle,
  email: "",
  phone: "",
  countryCode: 0,
  extension: "",
});

const validateEdit = useVuelidate(
  {
    name: rules.name,
    jobTitle: rules.jobTitle,
  },
  formEditContact,
);

const selectedPhone = ref<Phone | null>(null);
const selctedEmail = ref<Email | null>(null);

const handleEdit = async () => {
  for (const field of validateEdit.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  const contacts = new Set(
    formEditContact.contactType
      .concat(contactTypes.value)
      .map((contact) => contact.id),
  );

  if (contacts.size === 0) {
    toast.error("Debe seleccionar al menos un tipo de contacto");
    return;
  }

  const data = await updateContactAsync({
    contactTypeIds: [...contacts],
    name: formEditContact.name,
    jobTitle: formEditContact.jobTitle,
    id: props.contact.id,
  });

  if (data) {
    if (props.contact.emails.length > 0 && selctedEmail.value) {
      await updateContactEmailAsync({
        id: props.contact.id,
        emailId: selctedEmail.value.id,
        email: formEditContact.email,
        type: EmailTypes.Personal,
      });
    }

    if (props.contact.phones.length > 0 && selectedPhone.value) {
      await updateContactPhoneAsync({
        id: props.contact.id,
        phoneId: selectedPhone.value.id,
        phone: formEditContact?.phone,
        countryCodeId: formEditContact.countryCode,
        extension: formEditContact.extension,
        type: formEditContact.extension
          ? PhoneTypes.Oficina
          : PhoneTypes.Mobile,
      });
    }

    await queryClient.invalidateQueries(["hotel-contacts"]);
    modalEditContact.value = false;

    toast.success("Contacto actualizado correctamente");
    selectedPhone.value = null;
    selctedEmail.value = null;
  }
};

const actionDelete = ref(false);

const actionModalDelete = () => {
  actionDelete.value = !actionDelete.value;
};

const handleDelete = async (id: number) => {
  await deleteContactAsync({
    id: id,
  });

  await queryClient.invalidateQueries(["hotel-contacts"]);
  actionModalDelete();
};

const handleRestore = async (id: number) => {
  await restoreContactAsync({
    id: id,
  });

  await queryClient.invalidateQueries(["hotel-contacts"]);
};

const eliminateContactType = (id: number) => {
  formEditContact.contactType = formEditContact.contactType.filter(
    (contactType) => contactType.id !== id,
  );

  contactTypes.value = contactTypes.value.filter(
    (contactType) => contactType.id !== id,
  );
};

const filteredOptions = computed(() => {
  return filteredContactsType.value?.filter((contactType) => {
    return !formEditContact.contactType.some(
      (contact) => contact.id === contactType.id,
    );
  });
});

const formatPhone = (phone: Phone) => {
  return `+${phone.countryCode.code} ${phone.phone} ${
    phone.extension ? `ext. ${phone.extension}` : ""
  }`;
};
</script>

<template>
  <RoundedBox
    :class-name="`mb-4 flex md:flex-row flex-col gap-4 p-4 ${contact.active ? 'bg-white' : 'bg-gray-100'}`"
  >
    <div class="flex flex-col w-full">
      <h1 class="flex flex-col">
        {{ contact.name }}
      </h1>

      <div class="flex gap-2">
        <p>{{ contact.jobTitle }}</p>
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex flex-col gap-2" v-if="contact.emails.length < 1">
          <HasPermission
            :name-permission="'create-contacts-internal'"
            :entity-type="'App\\Models\\Hotel'"
          >
            <div class="flex gap-2 items-center">
              <Icon
                icon="ic:outline-email"
                class="text-2xl cursor-pointer"
                :class="'text-red-300 pointer-events-auto cursor-pointer'"
                v-tooltip="
                  contact.contactEmail
                    ? `${contact.phones.length}`
                    : 'Sin correos agregados'
                "
                v-on:click="actionViewEmail"
              />
            </div>
          </HasPermission>
        </div>

        <template v-for="contactEmail in contact.emails" :key="contactEmail.id">
          <div class="flex items-center gap-2">
            <Icon
              icon="ic:outline-email"
              class="text-2xl cursor-pointer"
              :class="'text-blue-500 pointer-events-none'"
            />
            <p>{{ contactEmail.email }}</p>
          </div>
        </template>

        <div class="flex flex-col gap-2" v-if="contact.phones.length < 2">
          <HasPermission
            :name-permission="'create-contacts-internal'"
            :entity-type="'App\\Models\\Hotel'"
          >
            <div class="flex gap-2 items-center">
              <Icon
                icon="ic:outline-phone"
                class="text-2xl cursor-pointer"
                :class="'text-red-300 pointer-events-auto cursor-pointer'"
                v-tooltip="
                  contact.contactPhone
                    ? `${contact.phones.length}`
                    : 'Sin teléfonos agregados'
                "
                v-on:click="actionViewPhone"
              />
              <p>{{ contact.contactPhone }}</p>
            </div>
          </HasPermission>
        </div>

        <template v-for="phoneContact in contact.phones" :key="phoneContact.id">
          <div class="flex items-center gap-2">
            <Icon
              :icon="
                phoneContact.type === 'celular'
                  ? 'mingcute:whatsapp-line'
                  : 'ic:outline-phone'
              "
              class="text-2xl"
              :class="'text-blue-500'"
            />

            <p>
              {{ formatPhone(phoneContact) }}
            </p>
          </div>
        </template>
      </div>
    </div>

    <div class="flex flex-col items-center justify-center w-full">
      <p
        class=""
        v-for="contactTypes in contact.contactTypes"
        :key="contactTypes.id"
      >
        {{ contactTypes.name }}
      </p>
    </div>

    <div
      class="w-full flex flex-row justify-between md:flex-col md:justify-center gap-10 md:items-end"
    >
      <HasPermission
        :name-permission="'update-contacts-internal'"
        :entity-type="'App\\Models\\Hotel'"
      >
        <StyledButton
          class="bg-blue-500 hover:bg-blue-900"
          v-if="contact.active"
        >
          <Icon
            icon="fluent-mdl2:edit-contact"
            class="text-2xl"
            :class="'text-white '"
            v-on:click="actionEditContact"
          />
        </StyledButton>
      </HasPermission>

      <HasPermission
        :name-permission="'delete-contacts-internal'"
        :entity-type="'App\\Models\\Hotel'"
      >
        <StyledButton class="bg-red-500 hover:bg-red-900" v-if="contact.active">
          <Icon
            icon="icon-park-outline:people-delete"
            class="text-2xl"
            :class="'text-white'"
            v-on:click="actionModalDelete"
          />
        </StyledButton>
      </HasPermission>
    </div>

    <HasPermission
      :name-permission="'restore-contacts-internal'"
      :entity-type="'App\\Models\\Hotel'"
    >
      <div>
        <StyledButton
          class="bg-green-500 hover:bg-green-900"
          v-if="!contact.active"
          @click="handleRestore(contact.id)"
        >
          <Icon icon="tdesign:refresh" class="text-2xl" :class="'text-white'" />
        </StyledButton>
      </div>
    </HasPermission>
  </RoundedBox>

  <!-- Modal edit Contact -->
  <ModalComponent
    :model-value="modalEditContact"
    @update:model-value="
      () => {
        actionEditContact();
        selectedPhone = null;
        selctedEmail = null;
      }
    "
  >
    <template #modalBody>
      <div class="p-5">
        <ShadowBox class="bg-white">
          <h1 class="text-2xl font-bold mb-2">Editar contacto</h1>

          <form @submit.prevent="handleEdit()" class="flex flex-col gap-3">
            <div class="grid grid-cols-3 gap-2">
              <div
                v-for="contact in formEditContact.contactType.concat(
                  contactTypes,
                )"
                :key="contact.id"
                class="bg-blue-300 rounded-md p-1.5 hover:bg-blue-500 transition-all cursor-pointer"
              >
                <div class="flex items-center justify-between gap-2">
                  <p class="text-white text-sm">
                    {{ contact.name }}
                  </p>
                  <button
                    @click="eliminateContactType(contact.id)"
                    class="text-red-500 hover:text-red-900 text-sm font-bold"
                    type="button"
                  >
                    X
                  </button>
                </div>
              </div>
            </div>

            <StyledCombobox
              label="Tipo de contactos"
              multiple
              v-model="contactTypes"
              :options="
                filteredOptions?.map((option) => {
                  return {
                    id: option.id,
                    name: option.name,
                  };
                }) || []
              "
            >
              <template #default="{ option }">
                <span>{{ option.name }}</span>
              </template>
            </StyledCombobox>

            <StyledInput
              v-model="formEditContact.name"
              label="Nombre"
              placeholder="Nombre"
              :is-required="true"
            />

            <StyledInput
              v-model="formEditContact.jobTitle"
              label="Puesto"
              placeholder="Nombre del puesto"
              :is-required="true"
            />

            <!-- Emails -->
            <div v-if="contact.emails.length > 0">
              <template
                v-for="contactEmail in contact.emails"
                :key="contactEmail.id"
              >
                <div class="flex items -center gap-2">
                  <Icon
                    icon="ic:outline-email"
                    class="text-2xl cursor-pointer"
                    :class="'text-blue-500 pointer-events-none'"
                  />
                  <p>{{ contactEmail.email }}</p>

                  <HasPermission
                    :name-permission="'update-contacts-internal'"
                    :entity-type="'App\\Models\\Hotel'"
                  >
                    <button
                      class="text-blue-500 hover:text-blue-900 text-sm font-bold"
                      type="button"
                      @click="
                        () => {
                          selctedEmail = contactEmail;
                          formEditContact.email = contactEmail.email;
                        }
                      "
                    >
                      <span class="material-symbols-outlined"> edit </span>
                    </button>
                  </HasPermission>
                </div>

                <div class="" v-if="selctedEmail?.id === contactEmail.id">
                  <div
                    class="flex md:flex-row flex-col items-center gap-2 mt-2"
                  >
                    <StyledInput
                      v-model="formEditContact.email"
                      label="Correo electrónico"
                      placeholder="correo electrónico"
                      :is-required="true"
                      class="w-full"
                      :disabled="isLoadingPhones"
                    />
                  </div>
                </div>
              </template>
            </div>

            <!-- Phones -->
            <div class="flex flex-col gap-2" v-if="contact.phones.length > 0">
              <template
                v-for="phoneContact in contact.phones"
                :key="phoneContact.id"
              >
                <div class="flex items-center gap-2">
                  <Icon
                    :icon="
                      phoneContact.type === 'celular'
                        ? 'mingcute:whatsapp-line'
                        : 'ic:outline-phone'
                    "
                    class="text-2xl"
                    :class="'text-blue-500'"
                  />

                  <p>
                    {{ formatPhone(phoneContact) }}
                  </p>

                  <button
                    v-if="selectedPhone?.id !== phoneContact.id"
                    class="text-blue-500 hover:text-blue-900 text-sm font-bold"
                    type="button"
                    @click="
                      () => {
                        selectedPhone = phoneContact;
                        formEditContact.phone = phoneContact.phone;
                        formEditContact.countryCode =
                          phoneContact.countryCode.id;
                        formEditContact.extension = phoneContact.extension;
                      }
                    "
                  >
                    <span class="material-symbols-outlined"> edit </span>
                  </button>
                </div>

                <div class="" v-if="selectedPhone?.id === phoneContact.id">
                  <div
                    class="flex md:flex-row flex-col items-center gap-2 mt-2"
                  >
                    <StyledSelect
                      v-model="formEditContact.countryCode"
                      label="Código de país"
                      :options="
                        countryCodes?.map((code) => ({
                          label: ` ${code.name} (+${code.code})`,
                          value: code.id,
                        })) || []
                      "
                      :is-required="true"
                      class="w-full md:w-1/3"
                      :loading-options="isLoadingPhones"
                    />

                    <StyledInput
                      v-model="formEditContact.phone"
                      label="Teléfono"
                      placeholder="Teléfono"
                      :is-required="true"
                      type="tel"
                      :max-length="10"
                      class="w-full md:w-1/3"
                      :disabled="isLoadingPhones"
                    />

                    <StyledInput
                      v-model="formEditContact.extension"
                      label="Extensión"
                      placeholder="Extensión"
                      type="text"
                      :max-length="4"
                      class="w-full md:w-1/3"
                      :disabled="isLoadingPhones"
                    />
                  </div>
                </div>
              </template>
            </div>

            <StyledButton
              class="bg-blue-500 hover:bg-blue-900 ml-auto mt-2 text-white"
              :disabled="
                updateContactLoading || updateEmailLoading || updatePhoneLoading
              "
              :loading="
                updateContactLoading || updateEmailLoading || updatePhoneLoading
              "
            >
              Guardar Cambios
            </StyledButton>
          </form>
        </ShadowBox>
      </div>
    </template>
  </ModalComponent>

  <!-- Modal eliminate contact -->
  <AlertComponent
    :model-value="actionDelete"
    type="danger"
    :dismiss="true"
    :on-confirm="() => handleDelete(props.contact.id)"
    :on-cancel="actionModalDelete"
    :loading="deleteContactLoading"
  >
    <template #title>
      <h3>Eliminar contacto: ({{ props.contact.name }})</h3>
    </template>

    <template #body>
      <p class="font-normal text-sm">
        Al eliminar este contacto, se eliminarán todos los datos asociados a él.
      </p>
    </template>

    <template #closeButton> Cancelar </template>

    <template #button> Eliminar </template>
  </AlertComponent>

  <!-- Modal create email -->
  <ModalComponent
    :model-value="modalViewEmail"
    @update:model-value="
      () => {
        actionViewEmail();
        formEmail.email = '';
      }
    "
  >
    <template #modalBody>
      <div class="p-5">
        <div>
          <form @submit.prevent="handleCreateEmail">
            <StyledInput
              v-model="formEmail.email"
              label="Correo electrónico"
              placeholder="Correo electrónico"
              type="text"
            />

            <StyledButton
              class="bg-blue-500 hover:bg-blue-900 ml-auto mt-2 text-white"
              :disabled="createContactEmailLoading"
              :loading="createContactEmailLoading"
            >
              Crear
            </StyledButton>
          </form>
        </div>
      </div>
    </template>
  </ModalComponent>

  <!-- Modal create Phone -->
  <ModalComponent
    :model-value="modalViewPhone"
    @update:model-value="
      () => {
        actionViewPhone();
        formPhone.phone = '';
        formPhone.countryCode = '';
      }
    "
  >
    <template #modalBody>
      <div class="p-5">
        <div>
          <form
            @submit.prevent="
              () => {
                handleCreatePhone();
              }
            "
          >
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
              <StyledSelect
                v-model="formPhone.countryCode"
                label="Código de país"
                :options="
                  countryCodes?.map((code) => ({
                    label: ` ${code.name} (+${code.code})`,
                    value: code.id,
                  })) || []
                "
                :is-required="true"
              />

              <StyledInput
                v-model="formPhone.phone"
                label="Teléfono"
                placeholder="Teléfono"
                :is-required="true"
                type="tel"
                :max-length="10"
              />

              <StyledInput
                v-model="formPhone.extension"
                label="Extensión"
                placeholder="Extensión"
                type="text"
                :max-length="4"
              />
            </div>

            <StyledButton
              class="bg-blue-500 hover:bg-blue-900 ml-auto mt-2 text-white"
              :disabled="createContactPhoneLoading"
              :loading="createContactPhoneLoading"
            >
              Crear
            </StyledButton>
          </form>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>
